// First, before anything that builds a schema: the browser runs zod without eval (see the file).
import '@/zod-config.ts'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom'
import { ApiRequestError } from '@/api/client.ts'
import { applyBrandToDocument } from '@/brand/active.ts'
import { AppShell } from '@/components/shell/AppShell.tsx'
import { RequireAuth } from '@/components/shell/RequireAuth.tsx'
import { RouteError } from '@/components/shell/RouteError.tsx'
import { I18nProvider, LocaleAccountSync } from '@/i18n/index.ts'
import { ActivityPage } from '@/pages/ActivityPage.tsx'
import { AgentAboutPage } from '@/pages/AgentAboutPage.tsx'
import { AgentBuilderPage } from '@/pages/AgentBuilderPage.tsx'
import { AgentsGalleryPage } from '@/pages/AgentsGalleryPage.tsx'
import { AgentsStudioPage } from '@/pages/AgentsStudioPage.tsx'
import { ChatPage } from '@/pages/ChatPage.tsx'
import { GovernancePage } from '@/pages/GovernancePage.tsx'
import { GroupsPage } from '@/pages/GroupsPage.tsx'
import { KnowledgePage } from '@/pages/KnowledgePage.tsx'
import { LoginPage } from '@/pages/LoginPage.tsx'
import { MakerAgentsPage } from '@/pages/MakerAgentsPage.tsx'
import { MemoryPage } from '@/pages/MemoryPage.tsx'
import { PeoplePage } from '@/pages/PeoplePage.tsx'
import { StartPage } from '@/pages/StartPage.tsx'
import '@/styles/index.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      // Retrying a 401 or a 403 just delays the redirect to sign-in and burns the user's
      // time; only genuinely transient failures are worth a second attempt.
      retry: (failureCount, error) => {
        if (error instanceof ApiRequestError && error.status >= 400 && error.status < 500) return false
        return failureCount < 2
      }
    }
  }
})

const router = createBrowserRouter([
  { path: '/login', element: <LoginPage />, errorElement: <RouteError fullPage /> },
  {
    path: '/',
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    // A failure in the shell itself; a page's own failure is caught one level down.
    errorElement: <RouteError fullPage />,
    children: [
      {
        // Pathless, so a page that throws is replaced inside the shell and the navigation around
        // it keeps working (D-66).
        errorElement: <RouteError />,
        children: [
          { index: true, element: <StartPage /> },
          { path: 'c/:id', element: <ChatPage /> },
          { path: 'knowledge', element: <KnowledgePage /> },
          { path: 'knowledge/:baseId', element: <KnowledgePage /> },
          { path: 'groups', element: <GroupsPage /> },
          { path: 'groups/:groupId', element: <GroupsPage /> },
          { path: 'memory', element: <MemoryPage /> },
          { path: 'activity', element: <ActivityPage /> },
          { path: 'governance', element: <GovernancePage /> },
          { path: 'people', element: <PeoplePage /> },
          // Static segments before ':agentId', or "new"/"mine"/"studio" become agent ids.
          // The old admin AgentsPage lives on inside AgentsStudioPage's Definitions tab.
          { path: 'agents', element: <AgentsGalleryPage /> },
          { path: 'agents/new', element: <AgentBuilderPage /> },
          { path: 'agents/mine', element: <MakerAgentsPage /> },
          { path: 'agents/studio', element: <AgentsStudioPage /> },
          { path: 'agents/:agentId', element: <AgentAboutPage /> },
          { path: 'agents/:agentId/edit', element: <AgentBuilderPage /> }
        ]
      }
    ]
  },
  { path: '*', element: <Navigate to="/" replace /> }
])

// The tab title, the description and the favicon, for the brand `boot.js` already stamped on
// the document. Outside React and before the first render: they belong to the document
// rather than to any route, and a component that owned them would have to be mounted on
// every one of them — including the sign-in page, which is the first thing a new deployment
// is judged by and the one route the shell does not wrap.
applyBrandToDocument()

const root = document.getElementById('root')
if (!root) throw new Error('#root is missing from index.html')

/*
 * `I18nProvider` sits between the query client and the router, and that position is not
 * incidental (PRD-27 D-28).
 *
 * Above the router, because `/login` and the catch-all are siblings of the authenticated
 * tree rather than children of it — a provider mounted in `AppShell`, which is the obvious
 * place, would leave the sign-in page in English. That page is the first thing a new
 * deployment is judged by, and the one screen a person reads before they have an account
 * whose language could have been looked up.
 *
 * Below the query client, because nothing about a request depends on the locale: no server
 * response in this product is localised, so switching language is a re-render and never a
 * refetch (LOC-007). Putting it above would suggest otherwise.
 */
createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <I18nProvider>
        {/* Renders nothing; keeps `users.locale` and the tab in step in both directions.
            A sibling rather than something inside the provider, so the provider itself needs
            no query client and stays mountable on its own. */}
        <LocaleAccountSync />
        <RouterProvider router={router} />
      </I18nProvider>
    </QueryClientProvider>
  </StrictMode>
)
